import adminAxios from '../Admin/adminAxios'
import { resolveFrontendApiBase } from '../utils/apiBase'
import type {
  Artifact,
  PackageRecord,
  ProductionRun,
  ProjectIntent,
  StoryIntentCreativeDraft,
  StoryIntentSeed,
  StoryBlueprint,
  StoryException,
  StoryProject,
  StoryCreationSettings,
} from './storyCreationTypes'

const API_BASE = resolveFrontendApiBase()
const base = `${API_BASE}/admin/story-projects`

export const storyCreationApi = {
  async settings(): Promise<StoryCreationSettings> {
    const response = await adminAxios.get(`${base}/settings`)
    return response.data
  },

  async listProjects(): Promise<StoryProject[]> {
    const response = await adminAxios.get(base)
    return response.data
  },

  async getProject(projectId: string): Promise<StoryProject> {
    const response = await adminAxios.get(`${base}/${encodeURIComponent(projectId)}`)
    return response.data
  },

  async getEstimate(projectId: string): Promise<{
    estimatedCostUsd: number
    maximumBudgetUsd: number
    withinBudget: boolean
    orchestrationModelId?: string
    storyModelId?: string
    evaluatorModelId?: string
    reasoningEffort?: string
  }> {
    const response = await adminAxios.get(`${base}/${encodeURIComponent(projectId)}/estimate`)
    return response.data
  },

  async createProject(intent: ProjectIntent): Promise<StoryProject> {
    const response = await adminAxios.post(base, { intent })
    return response.data
  },

  async assistIntent(seed: StoryIntentSeed): Promise<StoryIntentCreativeDraft> {
    const response = await adminAxios.post(`${base}/intent/assist`, { seed })
    return response.data
  },

  async updateIntent(projectId: string, intent: ProjectIntent, expectedRevision: number): Promise<StoryProject> {
    const response = await adminAxios.patch(`${base}/${encodeURIComponent(projectId)}/intent`, {
      intent,
      expected_revision: expectedRevision,
    })
    return response.data
  },

  async archiveProject(projectId: string, expectedRevision: number): Promise<StoryProject> {
    const response = await adminAxios.post(`${base}/${encodeURIComponent(projectId)}/archive`, {
      expected_revision: expectedRevision,
    })
    return response.data
  },

  async generateBlueprint(projectId: string): Promise<Artifact<StoryBlueprint>> {
    const response = await adminAxios.post(`${base}/${encodeURIComponent(projectId)}/blueprints/generate`)
    return response.data
  },

  async reviseBlueprint(projectId: string, instruction: string, expectedRevision: number): Promise<Artifact<StoryBlueprint>> {
    const response = await adminAxios.post(`${base}/${encodeURIComponent(projectId)}/blueprints/revise`, {
      instruction,
      expected_revision: expectedRevision,
    })
    return response.data
  },

  async listBlueprints(projectId: string): Promise<Array<Artifact<StoryBlueprint>>> {
    const response = await adminAxios.get(`${base}/${encodeURIComponent(projectId)}/artifacts/blueprint/versions`)
    return response.data
  },

  async createCreativeLock(projectId: string, blueprintArtifactId?: string): Promise<StoryProject> {
    const response = await adminAxios.post(`${base}/${encodeURIComponent(projectId)}/creative-lock`, {
      blueprint_artifact_id: blueprintArtifactId,
    })
    return response.data
  },

  async startRun(projectId: string, idempotencyKey: string): Promise<ProductionRun> {
    const response = await adminAxios.post(`${base}/${encodeURIComponent(projectId)}/production-runs`, {
      idempotency_key: idempotencyKey,
    })
    return response.data
  },

  async listRuns(projectId: string): Promise<ProductionRun[]> {
    const response = await adminAxios.get(`${base}/${encodeURIComponent(projectId)}/production-runs`)
    return response.data
  },

  async getRun(projectId: string, runId: string): Promise<ProductionRun> {
    const response = await adminAxios.get(
      `${base}/${encodeURIComponent(projectId)}/production-runs/${encodeURIComponent(runId)}`,
    )
    return response.data
  },

  async runAction(projectId: string, runId: string, action: 'pause' | 'resume' | 'cancel'): Promise<ProductionRun> {
    const response = await adminAxios.post(
      `${base}/${encodeURIComponent(projectId)}/production-runs/${encodeURIComponent(runId)}/${action}`,
    )
    return response.data
  },

  async listExceptions(projectId: string): Promise<StoryException[]> {
    const response = await adminAxios.get(`${base}/${encodeURIComponent(projectId)}/exceptions`)
    return response.data
  },

  async resolveException(
    projectId: string,
    exceptionId: string,
    resolution: string,
  ): Promise<StoryException> {
    const response = await adminAxios.post(
      `${base}/${encodeURIComponent(projectId)}/exceptions/${encodeURIComponent(exceptionId)}/resolve`,
      { resolution, resume_run: true },
    )
    return response.data
  },

  async listPackages(projectId: string): Promise<PackageRecord[]> {
    const response = await adminAxios.get(`${base}/${encodeURIComponent(projectId)}/packages`)
    return response.data
  },

  async downloadPackage(projectId: string, packageId: string, format: string): Promise<void> {
    const response = await adminAxios.get(
      `${base}/${encodeURIComponent(projectId)}/packages/${encodeURIComponent(packageId)}/${format}`,
      { responseType: 'blob' },
    )
    const disposition = String(response.headers?.['content-disposition'] || '')
    const match = disposition.match(/filename="?([^";]+)"?/i)
    const filename = match?.[1] || `manuscript.${format}`
    const url = URL.createObjectURL(response.data)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(url)
  },
}
